import { ReactNode, Suspense, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Category, Product } from "@/store/wallet/product.model";
import { WalletStore } from "@/store/wallet/wallet.store";
import { SettingsStore } from "@/store/settings/settings.store";

import { useResponsive } from "@/utils/useResponsive";

import { Navigation } from "@/components/ui/Navigation/Navigation";

import CheckIcon from "@/assets/svgs/check.svg?react";

import { useTranslation } from "react-i18next";
import { useBrandConfig } from "@/utils/useBrandConfig";
import { Pages } from "@/utils/routes.utils";
import { ServiceDrawer } from "@/components/ServiceDrawer/ServiceDrawer";
import { useGetSustainability, useListProductHistory } from "@/store/wallet/wallet.service";
import { UnlockProduct } from "@/components/LockProduct/UnlockProduct";
import { Modal } from "@/components/Modals/Modal.component";
import { getCustomComponent } from "@/utils/custom-components.utils";
import { ClipLoader } from "react-spinners";
import MobileCarousel from "@/components/Custom/MobileCarousel/default";
import DesktopCarousel from "@/components/Custom/DesktopCarousel/default";

type RedirectProps = {
  unlocked: boolean;
  type?: 'unlock' | 'gift' | 'resale';
}
export const ProductDetails = () => {
  const location = useLocation();
  const { state } = location;
  const { unlocked, type } = state as RedirectProps || {};

  const { productId } = useParams();
  const walletStore = WalletStore();
  const lang = SettingsStore.getState().getLanguage();
  const { isMobile, isTablet } = useResponsive();
  const { languages, name, digitalWalletConfig } = useBrandConfig();
  const defaultLangage = languages[0]

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);

  useEffect(() => {
    if (unlocked && type) {
      setUnlockModalOpen(true);
    }
  }, [unlocked, type])

  const [product, setProduct] = useState<Product>();
  const [images, setImages] = useState<string[]>([]);
  const [serviceDrawerOpen, setServiceDrawerOpen] = useState(false);
  const [serviceDrawerContent, setServiceDrawerContent] = useState<ReactNode>(null);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data: sustainabilityData } = useGetSustainability(product?.category?._id || '',);
  const { data: history } = useListProductHistory(product?.category?._id || '',);

  const parentPageTitle = digitalWalletConfig.translations?.[lang]?.title || digitalWalletConfig.translations?.[defaultLangage]?.title || t("header.WALLET");

  useEffect(() => {
    if (sustainabilityData) {
      walletStore.setSustainability(sustainabilityData);
    }
  }, [sustainabilityData]);

  useEffect(() => {
    if (history) {
      walletStore.setHistory(history);
    }
  }, [history]);

  useEffect(() => {
    const item = walletStore.products.find(el => el._id === productId) as Product;
    if (item) {
      setProduct(item);
      setImages(getImages(item.category));
    } else {
      navigate(Pages.HOME);
    }
  }, [navigate, productId, walletStore, walletStore.products]);

  const getImages = (category: Category) => {
    const { pictures } = category;
    return [
      pictures.main,
      pictures.secondary,
      pictures.tertiary,
      pictures.quaternary,
    ].filter((img): img is string => typeof img === "string");
  };

  const CustomProductInfos = useMemo(() => getCustomComponent({
    brandName: name,
    folder: 'ProductInfoSideBar',
  }), [name]);

  if (!product) return null;

  const { category: { translations } } = product;

  const productName = translations?.[lang]?.name ?? translations?.[defaultLangage]?.name ?? product.name;

  const unlockProduct = ({ gift, resale }: { gift?: boolean, resale?: boolean }) => {
    setServiceDrawerContent(
      <UnlockProduct
        productName={productName}
        productSku={product!.sku}
        image={product!.category.pictures.main}
        productId={product!._id}
        reference={product!.reference}
        gift={gift ? product?.gift?._id : undefined}
        resale={resale ? product?.resale?._id : undefined}
      />
    );
    setServiceDrawerOpen(true);
  }

  return isMobile || isTablet ? (
    <div className="flex flex-col min-h-vh">
      <div className="p-4"><Navigation page={parentPageTitle} subpage={t('details.productDetails')} /></div>
      <Suspense
        fallback={
          <div className="h-[50vh] flex flex-row items-center justify-center"><ClipLoader className="center" /></div>
        }
      >
        <MobileCarousel images={images} />
        <CustomProductInfos
          product={product}
          unlockProduct={unlockProduct}
          setServiceDrawerOpen={setServiceDrawerOpen}
          setServiceDrawerContent={setServiceDrawerContent}
        />
      </Suspense>
      <ServiceDrawer
        open={serviceDrawerOpen}
        setOpen={setServiceDrawerOpen}
        content={serviceDrawerContent}
        onExit={() => {
          if (isMobile) {
            setTimeout(() => {
              window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 300);
          }
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col py-6 px-8 flex-1">
      <div className="pb-4"><Navigation page={parentPageTitle} subpage={t('details.productDetails')} /></div>
      <div className="flex flex-row justify-between">
        <Suspense fallback={<div className="flex flex-1"></div>}>
          <DesktopCarousel
            images={images}
          />
          <CustomProductInfos
            product={product}
            unlockProduct={unlockProduct}
            setServiceDrawerOpen={setServiceDrawerOpen}
            setServiceDrawerContent={setServiceDrawerContent}
          />
        </Suspense>
      </div>
      <ServiceDrawer
        open={serviceDrawerOpen}
        setOpen={setServiceDrawerOpen}
        content={serviceDrawerContent}
      />
      <Modal
        title={t("unlock.modal.title")}
        subtitle={t("unlock.modal.subtitle")}
        open={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        closeBtn
        header={<CheckIcon />}
      />
    </div>
  );
};